import { Modal, ModalForm } from 'eone-ui/modal/modal';
import { ModalButtonType } from 'eone-ui/modal/modal-constants';
import { TablePanel } from 'eone-ui/panels/table-panel';
import { TableAlignment, TableFieldType, TableSortDirection } from 'eone-ui/tables/table-constants';
import { useTableState } from 'eone-ui/tables/useTableState';
import { useEffect, useMemo, useState } from 'react';

interface FieldData {
  fieldName: string;
  selected: boolean;
  fieldType?: string;
}

interface FieldsModalProps {
  Close: () => void;
  Result: (data: string[]) => void;
  ResultWithType?: (data: { fieldName: string; fieldType: string }[]) => void;
  Data: FieldData[];
  Title: string;
  EmptyMessage?: string;
  EmptyPrompts?: { Label: string; OnClick: any }[];
}

export function FieldsModal({
  Close,
  Title,
  Data,
  Result,
  ResultWithType,
  EmptyMessage,
  EmptyPrompts,
}: FieldsModalProps) {
  const [data, setData] = useState<FieldData[]>([]);

  useEffect(() => {
    if (!Data) return;
    setData(Data);
  }, [Data]);

  const selectAll = () => {
    setData((prevState) => prevState.map((row) => ({ ...row, selected: true })));
  };

  const deselectAll = () => {
    setData((prevState) => prevState.map((row) => ({ ...row, selected: false })));
  };

  const update = (id: string, fieldName: string, value: any) => {
    setData((prevState) =>
      prevState.map((row) => {
        if (row.fieldName == id) {
          row[fieldName] = value;
        }
        return row;
      }),
    );
  };

  const save = () => {
    const selected = data.filter((row) => row.selected);
    Result(selected.map((row) => row.fieldName));
    ResultWithType?.(
      selected.map((row) => ({ fieldName: row.fieldName, fieldType: row.fieldType ?? '' })),
    );
  };

  const getFooterButtons = () => {
    return [
      {
        Label: 'Select all',
        Type: ModalButtonType.Secondary,
        Visible: true,
        OnClick: selectAll,
      },
      {
        Label: 'Deselect all',
        Type: ModalButtonType.Secondary,
        Visible: true,
        OnClick: deselectAll,
      },
      {
        Label: 'Save',
        Type: ModalButtonType.Secondary,
        Visible: true,
        OnClick: save,
      },
    ];
  };

  const tableState = useTableState('fieldName', TableSortDirection.Ascending, [
    'fieldName',
    'fieldType',
    'selected',
  ]);

  const hasFieldTypes = Data.some((d) => d.fieldType != null && d.fieldType !== '');

  const columns = useMemo(
    () => [
      {
        Label: 'Name',
        FieldName: 'fieldName',
        FieldType: TableFieldType.String,
        Sortable: true,
        Searchable: true,
      },
      ...(hasFieldTypes
        ? [
            {
              Label: 'Type',
              FieldName: 'fieldType',
              FieldType: TableFieldType.String,
              Sortable: true,
              Searchable: true,
            },
          ]
        : []),
      {
        Label: 'Select',
        FieldName: 'selected',
        FieldType: TableFieldType.Checkbox,
        Sortable: false,
        Alignment: TableAlignment.Center,
        Searchable: false,
        Editable: true,
      },
    ],
    [hasFieldTypes],
  );

  return (
    <Modal Title={Title} Show Close={Close} FooterButtons={getFooterButtons()}>
      <ModalForm>
        <div style={{ maxHeight: 300, overflow: 'scroll' }}>
          <TablePanel
            Columns={columns}
            ChangeValue={update}
            Rows={data}
            IdField="fieldName"
            EmptyMessage={EmptyMessage ?? ''}
            EmptyPrompts={EmptyPrompts ?? []}
            Search
            TableState={tableState}
          />
        </div>
      </ModalForm>
    </Modal>
  );
}
